import React, { useState, useEffect, useCallback } from 'react';
import { getEntityContext, generateInsight, fetchSavedInsights } from '../services/dashboardService';
import { usePinToBoard } from '../contexts/PinToBoardContext';

// ── Colour helpers ────────────────────────────────────────────────────────────

const ENTITY_COLORS = {
  COMPANY:    '#3b82f6',
  PERSON:     '#22c55e',
  PROPERTY:   '#f59e0b',
  LEGAL_CASE: '#ef4444',
};
const entityColor = (type) =>
  ENTITY_COLORS[type?.toUpperCase?.()] || '#9ca3af';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * Clickable entity pill used wherever an entity appears in the body
 */
const EntityPill = ({ id, name, type, onNavigate }) => {
  if (!id) return <span className="ecw-rel-entity-name">{name}</span>;
  return (
    <button
      className="ecw-entity-pill"
      title={`Open context for ${name}`}
      onClick={() => onNavigate({ id, entity_name: name, entity_type: type })}
    >
      <span
        className="ecw-chip"
        style={{ background: entityColor(type) }}
      >
        {type || '?'}
      </span>
      <span className="ecw-entity-pill-name">{name}</span>
      <i className="fas fa-arrow-right ecw-entity-pill-arrow"></i>
    </button>
  );
};

/**
 * A single relationship group (incoming OR outgoing).
 * Entities inside are clickable via onNavigate.
 */
const RelationshipGroup = ({ group, direction, onNavigate }) => {
  const [open, setOpen] = useState(true);
  const arrow = direction === 'incoming' ? '←' : '→';

  return (
    <div className="ecw-rel-group">
      <button className="ecw-rel-group-toggle" onClick={() => setOpen(v => !v)}>
        <span className="ecw-rel-direction-arrow">{arrow}</span>
        <span className="ecw-rel-type-label">{group.relationship_type || 'RELATED'}</span>
        <span className="ecw-rel-count-badge">{group.count ?? group.entities?.length ?? 0}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} ecw-rel-chevron`}></i>
      </button>
      {open && (
        <ul className="ecw-rel-entity-list">
          {(group.entities || []).map((ent) => (
            <li key={ent.id} className="ecw-rel-entity-row">
              <EntityPill
                id={ent.id}
                name={ent.entity_name}
                type={ent.entity_type}
                onNavigate={onNavigate}
              />
              {ent.context && (
                <span className="ecw-rel-context" title={ent.context}>
                  <i className="fas fa-quote-right"></i>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const isLocalFile = (fileName) => {
  if (!fileName) return false;
  // If it's a URL (starts with http/https), it's NOT a local file
  const isUrl = fileName.startsWith('http://') || fileName.startsWith('https://');
  return !isUrl;
};

const getCleanFileName = (fileName) => {
  if (!fileName) return '';
  if (!isLocalFile(fileName)) return fileName;
  // Split by both / and \ for cross-platform compatibility
  const parts = fileName.split(/[\\/]/);
  return parts[parts.length - 1];
};

/**
 * Evidence source row — whole row is clickable (opens URL in new tab).
 * Supports both 'pdf' and 'webpage' source types.
 */
const EvidenceRow = ({ src }) => {
  const isWebpage = src.source_type === 'webpage';

  // For both pdf and webpage, the url comes from the `url` field.
  // For local pdf files, fall back to the file_name for display.
  const validUrl = (() => {
    let u = src.url || null;
    if (!u && src.file_name && !isLocalFile(src.file_name)) {
      // file_name may itself be a URL for legacy entries
      u = src.file_name;
    }
    if (u && !u.startsWith('http') && u.includes('.')) {
      u = 'https://' + u;
    }
    return u;
  })();

  // Display label: for webpages prefer url, for PDFs prefer clean file_name
  const displayLabel = isWebpage
    ? (src.url || src.file_name || String(src.source_id))
    : getCleanFileName(src.file_name) || src.url || String(src.source_id);

  // Choose the appropriate icon
  const iconClass = isWebpage ? 'fas fa-globe' : 'fas fa-file-pdf';

  const handleClick = (e) => {
    if (e.target.closest('a')) return;
    if (validUrl) window.open(validUrl, '_blank', 'noreferrer');
  };

  return (
    <div
      className={`ecw-evidence-row ${validUrl ? 'ecw-evidence-row--clickable' : ''}`}
      onClick={handleClick}
      role={validUrl ? 'link' : undefined}
      tabIndex={validUrl ? 0 : undefined}
      onKeyDown={validUrl ? (e) => { if (e.key === 'Enter') handleClick(e); } : undefined}
      title={validUrl ? `Open ${displayLabel}` : displayLabel}
    >
      <i className={`${iconClass} ecw-evidence-icon`}></i>
      <div className="ecw-evidence-body">
        <span className="ecw-evidence-name">
          {validUrl ? (
            <a
              href={validUrl}
              target="_blank"
              rel="noreferrer"
              className="ecw-evidence-link"
              onClick={(e) => e.stopPropagation()}
            >
              {displayLabel}
            </a>
          ) : (
            displayLabel
          )}
        </span>
        <span className="ecw-evidence-type">{src.source_type}</span>
      </div>
      <span className="ecw-evidence-connections">
        <i className="fas fa-link"></i> {src.connections}
      </span>
      {validUrl && (
        <i className="fas fa-external-link-alt ecw-evidence-open-icon"></i>
      )}
    </div>
  );
};

/**
 * Similar entity row — clickable, navigates to that entity's context.
 */
const SimilarRow = ({ item, onNavigate }) => {
  const pct  = Math.round((item.similarity_score || 0) * 100);
  const ent  = item.entity;
  const hasId = !!ent?.id;

  return (
    <div
      className={`ecw-similar-row ${hasId ? 'ecw-similar-row--clickable' : ''}`}
      onClick={hasId ? () => onNavigate({ id: ent.id, entity_name: ent.entity_name, entity_type: ent.entity_type }) : undefined}
      role={hasId ? 'button' : undefined}
      tabIndex={hasId ? 0 : undefined}
      onKeyDown={hasId ? (e) => { if (e.key === 'Enter') onNavigate({ id: ent.id, entity_name: ent.entity_name, entity_type: ent.entity_type }); } : undefined}
      title={hasId ? `Open context for ${ent?.entity_name}` : undefined}
    >
      <span
        className="ecw-chip"
        style={{ background: entityColor(ent?.entity_type) }}
      >
        {ent?.entity_type || '?'}
      </span>
      <span className="ecw-similar-name">{ent?.entity_name}</span>
      <div className="ecw-similar-score-wrap">
        <div className="ecw-similar-bar">
          <div className="ecw-similar-bar-fill" style={{ width: `${pct}%` }}></div>
        </div>
        <span className="ecw-similar-pct">{pct}%</span>
      </div>
      <span className="ecw-similar-shared">
        <i className="fas fa-share-nodes"></i> {item.shared_neighbors}
      </span>
      {hasId && <i className="fas fa-arrow-right ecw-similar-nav-icon"></i>}
    </div>
  );
};

// ── Property value renderer — handles objects, arrays, primitives ─────────────

const PropValue = ({ value, mono = false }) => {
  if (value == null) return <span className="ecw-prop-val ecw-prop-null">—</span>;

  if (typeof value === 'boolean') {
    return (
      <span className="ecw-prop-val">
        <span className={`ecw-prop-bool ${value ? 'ecw-prop-bool-yes' : 'ecw-prop-bool-no'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      </span>
    );
  }

  if (typeof value === 'number') {
    return <span className={`ecw-prop-val${mono ? ' ecw-prop-mono' : ''}`}>{value.toLocaleString()}</span>;
  }

  if (typeof value === 'string') {
    return <span className={`ecw-prop-val${mono ? ' ecw-prop-mono' : ''}`}>{value}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="ecw-prop-val ecw-prop-null">(empty)</span>;
    const allPrimitive = value.every((item) => item == null || typeof item !== 'object');
    if (allPrimitive) {
      return (
        <div className="ecw-prop-val ecw-prop-tags">
          {value.map((item, i) => (
            <span key={i} className="ecw-prop-tag">{item == null ? '—' : String(item)}</span>
          ))}
        </div>
      );
    }
    return (
      <pre className="ecw-prop-val ecw-prop-json">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="ecw-prop-val ecw-prop-null">(empty)</span>;
    const isFlat = entries.every(([, v]) => v == null || typeof v !== 'object');
    if (isFlat) {
      return (
        <div className="ecw-prop-sub-table">
          {entries.map(([k, v]) => (
            <div key={k} className="ecw-prop-sub-row">
              <span className="ecw-prop-sub-key">{k}</span>
              <span className="ecw-prop-sub-val">{v == null ? '—' : String(v)}</span>
            </div>
          ))}
        </div>
      );
    }
    return (
      <pre className="ecw-prop-val ecw-prop-json">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return <span className="ecw-prop-val">{String(value)}</span>;
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * EntityContextWindow
 *
 * Maintains an internal navigation stack so that clicking any entity
 * inside the modal navigates to that entity's context without closing.
 *
 * Props:
 *   entityId   – UUID of the root entity to load
 *   entityName – display name (shown while loading)
 *   entityType – display type (shown while loading)
 *   onClose    – callback to close the window
 */
const EntityContextWindow = ({ entityId, entityName, entityType, onClose }) => {
  const { pinItem } = usePinToBoard();

  // Navigation stack — each entry: { id, entity_name, entity_type }
  const [stack, setStack] = useState([
    { id: entityId, entity_name: entityName || '', entity_type: entityType || 'UNKNOWN' },
  ]);

  const current = stack[stack.length - 1];

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [activeTab, setActiveTab] = useState('connections');

  // ── Deep Analysis state ───────────────────────────────────────────────────
  const [deepAnalysis, setDeepAnalysis]   = useState(null);
  const [deepLoading, setDeepLoading]     = useState(false);  // actively generating
  const [deepSearching, setDeepSearching] = useState(false);  // checking for saved insights
  const [deepError, setDeepError]         = useState(null);

  // Load data whenever current entity in stack changes
  const load = useCallback(async () => {
    if (!current?.id) return;
    setLoading(true);
    setError(null);
    setData(null);
    const res = await getEntityContext(current.id);
    if (res.success) {
      setData(res.data);
    } else {
      setError(res.message || 'Failed to load entity context.');
    }
    setLoading(false);
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Reset to connections tab and clear deep analysis when navigating to a new entity
  useEffect(() => {
    setActiveTab('connections');
    setDeepAnalysis(null);
    setDeepError(null);
    setDeepSearching(false);
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-load saved insights when Deep Analysis tab opens ─────────────────
  // Fires whenever the user switches to the deep_analysis tab or navigates to a new entity.
  // Silently checks the saved-insights endpoint; if a previous analysis exists for this
  // entity it is displayed immediately. If not, the "Generate" empty state is shown.
  useEffect(() => {
    if (activeTab !== 'deep_analysis') return;
    if (!current?.id) return;
    // Skip if we already have data or are in the middle of generating/searching
    if (deepAnalysis !== null || deepLoading) return;

    let cancelled = false;

    const checkSaved = async () => {
      setDeepSearching(true);
      try {
        // Fetch the most recent saved entity_summary insights (up to 20)
        const res = await fetchSavedInsights('entity_summary', 20, 0);
        if (!cancelled && res.success && Array.isArray(res.data) && res.data.length > 0) {
          // Find the most recent entry whose entityIds contains the current entity's id
          // The API returns results ordered newest-first, so the first match is the latest
          const match = res.data.find(
            (d) => Array.isArray(d.entityIds) && d.entityIds.includes(current.id)
          );
          if (match) setDeepAnalysis(match);
        }
      } catch {
        // Silent fail — just show the empty "Generate Analysis" state
      }
      if (!cancelled) setDeepSearching(false);
    };

    checkSaved();
    return () => { cancelled = true; };
  }, [activeTab, current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape; navigate back on Alt+← 
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (stack.length > 1) { setStack(s => s.slice(0, -1)); }
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, stack.length]);

  // Navigate into a connected / similar entity
  const handleNavigate = useCallback((entity) => {
    if (!entity?.id) return;
    setStack(s => [
      ...s,
      { id: entity.id, entity_name: entity.entity_name || '', entity_type: entity.entity_type || 'UNKNOWN' },
    ]);
  }, []);

  const handleBack = () => setStack(s => s.slice(0, -1));

  // ── Generate Deep Entity Analysis ─────────────────────────────────────────
  const handleGenerateDeepAnalysis = useCallback(async () => {
    if (!current?.id) return;
    setDeepLoading(true);
    setDeepError(null);
    const res = await generateInsight('entity_summary', [current.id]);
    if (res.success) {
      setDeepAnalysis(res.data);
    } else {
      setDeepError(res.message || 'Failed to generate deep analysis.');
    }
    setDeepLoading(false);
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived display values — prefer API response over stack entry
  const entity  = data?.entity;
  const display = {
    name: entity?.entity_name || current.entity_name || '—',
    type: entity?.entity_type || current.entity_type || 'UNKNOWN',
    id:   entity?.id           || current.id,
  };
  const color = entityColor(display.type);

  const incoming = data?.directConnections?.incoming || [];
  const outgoing = data?.directConnections?.outgoing || [];
  const evidence = data?.evidenceSources  || [];
  const similar  = data?.similarEntities  || [];
  const second   = data?.secondDegreeConnections ?? 0;

  const tabs = [
    { id: 'connections',   label: 'Connections',   icon: 'fas fa-project-diagram', count: incoming.length + outgoing.length },
    { id: 'evidence',      label: 'Evidence',       icon: 'fas fa-file-alt',        count: evidence.length },
    { id: 'similar',       label: 'Similar',        icon: 'fas fa-layer-group',     count: similar.length  },
    { id: 'properties',    label: 'Properties',     icon: 'fas fa-sliders-h',       count: null            },
    { id: 'deep_analysis', label: 'Deep Analysis',  icon: 'fas fa-brain',           count: null            },
  ];

  // Pin current entity to whiteboard
  const handlePin = (e) => {
    e.stopPropagation();
    pinItem(
      {
        item_type: 'knowledge_node',
        ref_id:    String(display.id),
        title:     display.name,
        metadata:  { entity_type: display.type },
      },
      e.currentTarget,
    );
  };

  return (
    <div className="ecw-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ecw-modal">

        {/* ── Header ── */}
        <div className="ecw-header" style={{ borderTop: `4px solid ${color}` }}>
          {/* Back button when navigating deeper */}
          {stack.length > 1 && (
            <button className="ecw-back-btn" onClick={handleBack} title="Go back">
              <i className="fas fa-arrow-left"></i>
            </button>
          )}

          <div className="ecw-header-left">
            <span className="ecw-type-chip" style={{ background: color }}>
              {display.type}
            </span>
            <h2 className="ecw-title">{display.name}</h2>
          </div>

          {/* Breadcrumb depth indicator */}
          {stack.length > 1 && (
            <span className="ecw-breadcrumb-depth" title={stack.map(s => s.entity_name).join(' → ')}>
              {stack.length} deep
            </span>
          )}

          <button className="ecw-close-btn" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* ── Loading / Error states ── */}
        {loading && (
          <div className="ecw-loading">
            <div className="ecw-spinner"></div>
            <p>Loading entity context…</p>
          </div>
        )}

        {!loading && error && (
          <div className="ecw-error">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button className="btn-secondary" onClick={load}>Retry</button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* ── Stats banner ── */}
            <div className="ecw-stats-bar">
              <div className="ecw-stat">
                <span className="ecw-stat-value">
                  {incoming.reduce((s, g) => s + (g.count ?? g.entities?.length ?? 0), 0)}
                </span>
                <span className="ecw-stat-label">Incoming</span>
              </div>
              <div className="ecw-stat">
                <span className="ecw-stat-value">
                  {outgoing.reduce((s, g) => s + (g.count ?? g.entities?.length ?? 0), 0)}
                </span>
                <span className="ecw-stat-label">Outgoing</span>
              </div>
              <div className="ecw-stat">
                <span className="ecw-stat-value">{second.toLocaleString()}</span>
                <span className="ecw-stat-label">2° Connections</span>
              </div>
              <div className="ecw-stat">
                <span className="ecw-stat-value">{evidence.length}</span>
                <span className="ecw-stat-label">Sources</span>
              </div>
              {entity?.created_at && (
                <div className="ecw-stat">
                  <span className="ecw-stat-value ecw-stat-date">{formatDate(entity.created_at)}</span>
                  <span className="ecw-stat-label">First seen</span>
                </div>
              )}

              {/* ── Pin to Whiteboard ── */}
              <div className="ecw-stats-actions">
                <button
                  className="ecw-pin-btn"
                  onClick={handlePin}
                  title="Pin to Whiteboard"
                >
                  <i className="fas fa-thumbtack"></i>
                  <span>Pin</span>
                </button>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div className="ecw-tabs">
              {tabs.map(t => (
                <button
                  key={t.id}
                  className={`ecw-tab ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <i className={t.icon}></i>
                  <span>{t.label}</span>
                  {t.count != null && <span className="ecw-tab-count">{t.count}</span>}
                </button>
              ))}
            </div>

            {/* ── Tab content ── */}
            <div className="ecw-body">

              {/* Connections tab */}
              {activeTab === 'connections' && (
                <div className="ecw-section">
                  {incoming.length === 0 && outgoing.length === 0 && (
                    <p className="ecw-empty">No direct connections found.</p>
                  )}
                  {incoming.length > 0 && (
                    <div className="ecw-conn-group">
                      <h4 className="ecw-section-title">
                        <i className="fas fa-arrow-down"></i> Incoming
                      </h4>
                      {incoming.map((g, i) => (
                        <RelationshipGroup key={i} group={g} direction="incoming" onNavigate={handleNavigate} />
                      ))}
                    </div>
                  )}
                  {outgoing.length > 0 && (
                    <div className="ecw-conn-group">
                      <h4 className="ecw-section-title">
                        <i className="fas fa-arrow-up"></i> Outgoing
                      </h4>
                      {outgoing.map((g, i) => (
                        <RelationshipGroup key={i} group={g} direction="outgoing" onNavigate={handleNavigate} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Evidence tab */}
              {activeTab === 'evidence' && (
                <div className="ecw-section">
                  {evidence.length === 0 && (
                    <p className="ecw-empty">No evidence sources found for this entity.</p>
                  )}
                  {evidence.map((src, i) => (
                    <EvidenceRow key={i} src={src} />
                  ))}
                </div>
              )}

              {/* Similar tab */}
              {activeTab === 'similar' && (
                <div className="ecw-section">
                  {similar.length === 0 && (
                    <p className="ecw-empty">No similar entities found.</p>
                  )}
                  {similar.map((item, i) => (
                    <SimilarRow key={i} item={item} onNavigate={handleNavigate} />
                  ))}
                </div>
              )}

              {/* Properties tab */}
              {activeTab === 'properties' && (
                <div className="ecw-section">
                  <div className="ecw-props-table">
                    <div className="ecw-prop-row">
                      <span className="ecw-prop-key">ID</span>
                      <span className="ecw-prop-val ecw-prop-mono">{entity?.id || entityId}</span>
                    </div>
                    <div className="ecw-prop-row">
                      <span className="ecw-prop-key">Type</span>
                      <span className="ecw-prop-val">{entity?.entity_type || display.type}</span>
                    </div>
                    {entity?.created_at && (
                      <div className="ecw-prop-row">
                        <span className="ecw-prop-key">Created</span>
                        <span className="ecw-prop-val">{formatDate(entity.created_at)}</span>
                      </div>
                    )}
                    {entity?.updated_at && (
                      <div className="ecw-prop-row">
                        <span className="ecw-prop-key">Updated</span>
                        <span className="ecw-prop-val">{formatDate(entity.updated_at)}</span>
                      </div>
                    )}
                  </div>

                  {entity?.properties && Object.keys(entity.properties).length > 0 ? (
                    <div className="ecw-props-table ecw-props-extra">
                      {Object.entries(entity.properties).map(([k, v]) => (
                        <div className="ecw-prop-row" key={k}>
                          <span className="ecw-prop-key">{k}</span>
                          <PropValue value={v} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    !entity?.created_at && !entity?.updated_at && (
                      <p className="ecw-empty">No additional properties.</p>
                    )
                  )}
                </div>
              )}

              {/* Deep Analysis tab */}
              {activeTab === 'deep_analysis' && (
                <div className="ecw-section">

                  {/* ── Searching for saved analysis ── */}
                  {deepSearching && !deepAnalysis && (
                    <div className="ecw-loading">
                      <div className="ecw-spinner"></div>
                      <p>Checking for previous analysis…</p>
                    </div>
                  )}

                  {/* ── Empty state: no analysis found or yet generated ── */}
                  {!deepAnalysis && !deepLoading && !deepError && !deepSearching && (
                    <div className="ecw-da-empty">
                      <i className="fas fa-brain"></i>
                      <h4>Deep Entity Analysis</h4>
                      <p>
                        Generate a comprehensive AI-powered analysis covering identity,
                        network centrality, market signals, opportunities, risks, and
                        due diligence flags.
                      </p>
                      <button className="ecw-da-generate-btn" onClick={handleGenerateDeepAnalysis}>
                        <i className="fas fa-wand-magic-sparkles"></i> Generate Analysis
                      </button>
                    </div>
                  )}

                  {/* ── Actively generating ── */}
                  {deepLoading && (
                    <div className="ecw-loading">
                      <div className="ecw-spinner"></div>
                      <p>Generating deep analysis…</p>
                    </div>
                  )}

                  {/* ── Error ── */}
                  {!deepLoading && deepError && (
                    <div className="ecw-error">
                      <i className="fas fa-exclamation-triangle"></i>
                      <p>{deepError}</p>
                      <button onClick={handleGenerateDeepAnalysis}>Retry</button>
                    </div>
                  )}

                  {/* ── Results ── */}
                  {!deepLoading && !deepError && deepAnalysis && (() => {
                    const ins = deepAnalysis.insight || {};
                    return (
                      <div className="ecw-da-content">

                        {/* Summary overview */}
                        {ins.summary && (
                          <div className="ecw-da-overview">
                            <p>{ins.summary}</p>
                          </div>
                        )}

                        {/* Identity Analysis */}
                        {ins.identity_analysis && (
                          <div className="ecw-da-section">
                            <h4 className="ecw-da-section-title">
                              <i className="fas fa-id-card"></i> Identity
                            </h4>
                            <div className="ecw-da-table">
                              {ins.identity_analysis.classification && (
                                <div className="ecw-da-row">
                                  <span className="ecw-da-key">Classification</span>
                                  <span className="ecw-da-val">{ins.identity_analysis.classification}</span>
                                </div>
                              )}
                              {ins.identity_analysis.role && (
                                <div className="ecw-da-row">
                                  <span className="ecw-da-key">Role</span>
                                  <span className="ecw-da-val">{ins.identity_analysis.role}</span>
                                </div>
                              )}
                              {ins.identity_analysis.market_context && (
                                <div className="ecw-da-row">
                                  <span className="ecw-da-key">Market Context</span>
                                  <span className="ecw-da-val">{ins.identity_analysis.market_context}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Network Analysis */}
                        {ins.network_analysis && (
                          <div className="ecw-da-section">
                            <h4 className="ecw-da-section-title">
                              <i className="fas fa-project-diagram"></i> Network Analysis
                            </h4>
                            {ins.network_analysis.centrality && (
                              <p className="ecw-da-text">{ins.network_analysis.centrality}</p>
                            )}
                            {ins.network_analysis.key_relationships?.length > 0 && (
                              <div className="ecw-da-list-group">
                                <p className="ecw-da-list-label">Key Relationships</p>
                                <ul className="ecw-da-list">
                                  {ins.network_analysis.key_relationships.map((r, i) => (
                                    <li key={i} className="ecw-da-list-item ecw-da-relationship">{r}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {ins.network_analysis.second_degree_reach && (
                              <p className="ecw-da-text ecw-da-subdued">{ins.network_analysis.second_degree_reach}</p>
                            )}
                          </div>
                        )}

                        {/* Intelligence Value */}
                        {ins.intelligence_value && (
                          <div className="ecw-da-section">
                            <h4 className="ecw-da-section-title">
                              <i className="fas fa-chart-line"></i> Intelligence Value
                            </h4>
                            {ins.intelligence_value.market_signals?.length > 0 && (
                              <div className="ecw-da-list-group">
                                <p className="ecw-da-list-label ecw-da-label-signal">
                                  <i className="fas fa-signal"></i> Market Signals
                                </p>
                                <ul className="ecw-da-list">
                                  {ins.intelligence_value.market_signals.map((s, i) => (
                                    <li key={i} className="ecw-da-list-item ecw-da-signal">{s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {ins.intelligence_value.opportunities?.length > 0 && (
                              <div className="ecw-da-list-group">
                                <p className="ecw-da-list-label ecw-da-label-opportunity">
                                  <i className="fas fa-lightbulb"></i> Opportunities
                                </p>
                                <ul className="ecw-da-list">
                                  {ins.intelligence_value.opportunities.map((o, i) => (
                                    <li key={i} className="ecw-da-list-item ecw-da-opportunity">{o}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {ins.intelligence_value.risks?.length > 0 && (
                              <div className="ecw-da-list-group">
                                <p className="ecw-da-list-label ecw-da-label-risk">
                                  <i className="fas fa-exclamation-circle"></i> Risks
                                </p>
                                <ul className="ecw-da-list">
                                  {ins.intelligence_value.risks.map((r, i) => (
                                    <li key={i} className="ecw-da-list-item ecw-da-risk">{r}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Recommendations */}
                        {ins.recommendations?.length > 0 && (
                          <div className="ecw-da-section">
                            <h4 className="ecw-da-section-title">
                              <i className="fas fa-tasks"></i> Recommendations
                            </h4>
                            <ol className="ecw-da-rec-list">
                              {ins.recommendations.map((r, i) => (
                                <li key={i} className="ecw-da-rec-item">{r}</li>
                              ))}
                            </ol>
                          </div>
                        )}

                        {/* Due Diligence Flags */}
                        {ins.due_diligence_flags?.length > 0 && (
                          <div className="ecw-da-section ecw-da-flags-section">
                            <h4 className="ecw-da-section-title ecw-da-flags-title">
                              <i className="fas fa-flag"></i> Due Diligence Flags
                            </h4>
                            <ul className="ecw-da-list">
                              {ins.due_diligence_flags.map((f, i) => (
                                <li key={i} className="ecw-da-list-item ecw-da-flag">{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Footer — shows date, tokens and a refresh button */}
                        <div className="ecw-da-footer">
                          <button className="ecw-da-regen-btn" onClick={handleGenerateDeepAnalysis} disabled={deepLoading}>
                            <i className="fas fa-sync-alt"></i> Refresh Analysis
                          </button>
                          <div className="ecw-da-footer-meta">
                            {(deepAnalysis.createdAt || deepAnalysis.created_at) && (
                              <span className="ecw-da-tokens" title="When this analysis was generated">
                                <i className="fas fa-clock"></i>{' '}
                                {formatDate(deepAnalysis.createdAt || deepAnalysis.created_at)}
                              </span>
                            )}
                            {deepAnalysis.tokensUsed != null && (
                              <span className="ecw-da-tokens">{deepAnalysis.tokensUsed} tokens</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EntityContextWindow;
